import { useState, useEffect, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, limit, serverTimestamp, runTransaction
} from 'firebase/firestore';
import { db } from '../services/firebase-init';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { generateIntakePDF, printIntakeAsPDF } from '../pdf/intakePDF';
import {
  AppWindow, Package, Wrench, ClipboardList, CheckCircle2, XCircle, AlertTriangle,
  Trash2, Loader2, Send, Printer, ArrowLeft, ChevronRight,
} from 'lucide-react';
import { noAutofill, noAutofillContainer } from '../utils/noAutofill';

const INK    = '#2B2620';
const BROWN  = '#8B4513'; // WALNUT
const MOSS   = '#3B6B44';
const AMBER  = '#A6752A'; // OCHRE
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const card     = { background: PAPER, borderRadius: '16px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden', marginBottom: '20px' };
const mkBtn    = (variant = 'primary', disabled = false) => {
  const base = { padding: '8px 18px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s', opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px' };
  if (variant === 'primary') return { ...base, background: BROWN, color: 'white', boxShadow: disabled ? 'none' : '0 2px 8px rgba(139,69,19,0.25)' };
  if (variant === 'ghost')   return { ...base, background: 'rgba(139,69,19,0.07)', color: BROWN };
  if (variant === 'danger')  return { ...base, background: 'rgba(161,61,45,0.08)', color: RUST };
  if (variant === 'teal')    return { ...base, background: disabled ? 'rgba(59,107,68,0.1)' : MOSS, color: disabled ? '#aaa' : 'white', boxShadow: disabled ? 'none' : '0 2px 8px rgba(59,107,68,0.3)' };
  return base;
};
const inp = { padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.2)', fontSize: '13px', fontFamily: FONT_SANS, outline: 'none', background: '#faf8f5', color: INK, width: '100%', boxSizing: 'border-box' };
const FL = ({ label, children, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', ...style }}>
    <div style={{ fontSize: '10px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    {children}
  </div>
);

const TOAST_STYLES = {
  success: { bg: 'rgba(59,107,68,0.95)',  Icon: CheckCircle2 },
  warning: { bg: 'rgba(166,117,42,0.95)', Icon: AlertTriangle },
  error:   { bg: 'rgba(161,61,45,0.95)',  Icon: XCircle },
};
function Toast({ toast }) {
  if (!toast) return null;
  const s = TOAST_STYLES[toast.kind] || TOAST_STYLES.success;
  return (
    <div style={{ position:'fixed', bottom:'28px', left:'50%', transform:'translateX(-50%)', display:'flex', alignItems:'center', gap:'8px', background: s.bg, color:'#fff', padding:'10px 22px', borderRadius:'99px', fontSize:'13px', fontWeight:'600', fontFamily: FONT_SANS, zIndex:9999, whiteSpace:'nowrap', boxShadow:'0 6px 24px rgba(0,0,0,.2)' }}>
      <s.Icon size={15} strokeWidth={2} />
      {toast.message}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const date = d?.toDate ? d.toDate() : new Date(d);
  return date.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function todayKey() {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  const yyyy = now.getFullYear(), mm = String(now.getMonth()+1).padStart(2,'0'), dd = String(now.getDate()).padStart(2,'0');
  return { counterKey: `${yyyy}-${mm}-${dd}`, display: `${mm}${dd}${yyyy}` };
}
function getFlags(i) {
  const f = [];
  if (i.requestTelephoned)    f.push('Telephoned');
  if (i.requestReturnCall)    f.push('Return your call');
  if (i.requestPleaseCall)    f.push('Please call');
  if (i.requestWillCallAgain) f.push('Will call again');
  if (i.requestCameToSeeYou)  f.push('Came to see you');
  if (i.requestWantsToSeeYou) f.push('Wants to see you');
  return f;
}
function initials(name) {
  return (name || '?').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

async function createIntake(form) {
  const { counterKey, display } = todayKey();
  const counterRef = doc(db, 'intakeCounters', counterKey);
  const id = await runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const next = snap.exists() ? (snap.data().seq || 0) + 1 : 1;
    tx.set(counterRef, { seq: next, updatedAt: serverTimestamp() });
    return `TWP-INTAKE-${display}-${String(next).padStart(3, '0')}`;
  });
  const { setDoc } = await import('firebase/firestore');
  await setDoc(doc(db, 'intakes', id), { ...form, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  return id;
}

const FLAG_KEYS = [
  ['requestTelephoned','Telephoned'],['requestReturnCall','Return your call'],
  ['requestPleaseCall','Please call'],['requestWillCallAgain','Will call again'],
  ['requestCameToSeeYou','Came to see you'],['requestWantsToSeeYou','Wants to see you'],
];

// ── INQUIRY TYPE ──────────────────────────────────────────────────────────────
const INQUIRY_TYPES = [
  { value: 'windows_doors', label: 'Windows / Doors', Icon: AppWindow, rep: 'AJ',     color: BROWN,     printOnly: false },
  { value: 'mill_work',     label: 'Mill Work',       Icon: Package,   rep: 'Mike',   color: '#3B6B44', printOnly: false },
  { value: 'repair',        label: 'Repair',          Icon: Wrench,    rep: 'Justin', color: AMBER,     printOnly: true  },
];

// Repair type options — shown only when inquiryType === 'repair'
const REPAIR_TYPES = [
  { value: 'windows', label: 'Windows' },
  { value: 'siding',  label: 'Siding'  },
  { value: 'door',    label: 'Door'    },
  { value: 'other',   label: 'Other'   },
];

function InquiryTypePicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      {INQUIRY_TYPES.map(t => {
        const active = value === t.value;
        return (
          <button key={t.value} type="button" onClick={() => onChange(t.value)}
            style={{ flex: 1, padding: '14px 12px', borderRadius: '12px', border: `2px solid ${active ? t.color : 'rgba(139,69,19,0.15)'}`, background: active ? t.color : PAPER, color: active ? 'white' : '#8C8275', cursor: 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s', textAlign: 'center' }}>
            <div style={{ marginBottom: '4px' }}><t.Icon size={22} strokeWidth={2} /></div>
            <div style={{ fontSize: '13px', fontWeight: '700' }}>{t.label}</div>
            <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>→ {t.rep}{t.printOnly ? ' (print)' : ''}</div>
          </button>
        );
      })}
    </div>
  );
}

// ── REPAIR-ONLY FIELD BLOCK ───────────────────────────────────────────────────
// Renders Repair Type checkboxes + Best Time to Call + AM/PM picker.
// Used by both NewIntake and IntakeDetail edit mode.
function RepairFieldsBlock({ repairTypes, bestTimeToCall, bestTimeAmPm, onToggleType, onChangeBestTime, onChangeAmPm }) {
  return (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:AMBER, marginBottom:'7px' }}>
        <Wrench size={11} strokeWidth={2} /> Repair Details <span style={{ color:'#A13D2D' }}>*</span>
      </div>
      <div style={{ ...card, padding:'16px 18px', border:`1.5px solid ${AMBER}33`, background:'#fffaf3', marginBottom:'20px' }}>

        {/* Repair Type — multi-select */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#8C8275', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.08em' }}>
            Repair Type <span style={{ color:'#A13D2D' }}>*</span>
            <span style={{ color:'#8C8275', fontWeight:'500', textTransform:'none', marginLeft:'6px', fontSize:'10px' }}>(select one or more)</span>
          </div>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {REPAIR_TYPES.map(rt => {
              const active = !!(repairTypes && repairTypes[rt.value]);
              return (
                <button
                  key={rt.value}
                  type="button"
                  onClick={() => onToggleType(rt.value)}
                  style={{
                    padding: '9px 16px',
                    borderRadius: '10px',
                    border: `1.5px solid ${active ? AMBER : 'rgba(180,83,9,0.2)'}`,
                    background: active ? AMBER : '#fff',
                    color: active ? '#fff' : '#8C8275',
                    fontSize: '13px',
                    fontWeight: '600',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    transition: 'all 0.14s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                  }}>
                  <div style={{
                    width:'14px', height:'14px', borderRadius:'3px', flexShrink:0,
                    border:`1.5px solid ${active?'#fff':'#cfc5b8'}`,
                    background: active ? 'transparent' : '#fff',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    {active && <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  {rt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Best Time to Call — free text + AM/PM toggle */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'12px', alignItems:'end' }}>
          <FL label="Best Time to Call">
            <input
              value={bestTimeToCall || ''}
              onChange={e => onChangeBestTime(e.target.value)}
              placeholder="e.g. After 5pm, mornings, weekends…"
              style={inp} />
          </FL>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#8C8275', textTransform: 'uppercase', letterSpacing: '0.06em' }}>AM / PM</div>
            <div style={{ display:'flex', gap:'6px' }}>
              {['AM','PM'].map(x => {
                const active = bestTimeAmPm === x;
                return (
                  <button
                    key={x}
                    type="button"
                    onClick={() => onChangeAmPm(active ? '' : x)}
                    style={{
                      padding:'8px 16px', borderRadius:'8px',
                      border:`1.5px solid ${active ? AMBER : 'rgba(180,83,9,0.2)'}`,
                      background: active ? AMBER : '#fff',
                      color: active ? '#fff' : '#8C8275',
                      fontSize:'12px', fontWeight:'700', fontFamily:'inherit',
                      cursor:'pointer', minWidth:'44px',
                    }}>
                    {x}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ intakes, onNew, onViewAll, onSelect }) {
  const now = new Date(), tod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const wk = new Date(tod); wk.setDate(wk.getDate() - wk.getDay());
  let t = 0, w = 0;
  intakes.forEach(i => {
    const d = i.createdAt?.toDate ? i.createdAt.toDate() : new Date(i.createdAt || 0);
    if (d >= tod) t++; if (d >= wk) w++;
  });
  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: '#2B2620', marginBottom: '4px' }}>Office Intake</div>
        <div style={{ fontSize: '13px', color: '#8C8275' }}>Record walk-ins, phone calls, and email inquiries</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '24px' }}>
        {[{ label: 'Today', val: t }, { label: 'This Week', val: w }].map(s => (
          <div key={s.label} style={{ background: PAPER, border: '1px solid rgba(139,69,19,0.1)', borderRadius: '14px', padding: '18px 20px', boxShadow: '0 2px 8px rgba(139,69,19,0.05)' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8C8275', marginBottom: '8px' }}>{s.label}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '34px', fontWeight: 600, color: BROWN, letterSpacing: '-0.5px', lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: '11px', color: '#8C8275', marginTop: '4px' }}>intakes recorded</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8C8275' }}>Recent Intakes</div>
        <button style={mkBtn('ghost')} onClick={onViewAll}>View All →</button>
      </div>
      {intakes.length === 0 ? (
        <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
          <ClipboardList size={36} strokeWidth={1.5} color={STONE} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#2B2620', marginBottom: '5px' }}>No intakes yet</div>
          <div style={{ fontSize: '13px', color: '#8C8275' }}>Tap + New Intake to get started</div>
        </div>
      ) : (
        <div style={card}>
          {intakes.slice(0, 12).map((i, idx) => {
            const inquiryType = INQUIRY_TYPES.find(t => t.value === i.inquiryType);
            return (
              <div key={i.id} onClick={() => onSelect(i)}
                style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '13px 18px', cursor: 'pointer', borderBottom: idx < Math.min(intakes.length, 12) - 1 ? '1px solid rgba(139,69,19,0.06)' : 'none', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fdf7f2'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0, background: '#fdf5ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800', color: BROWN }}>{initials(i.customerName)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#2B2620', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.customerName || '—'}</div>
                  <div style={{ fontSize: '11px', color: '#8C8275', display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ padding: '1px 7px', borderRadius: '99px', background: '#fdf5ee', color: BROWN, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>{i.contactType || 'walk-in'}</span>
                    {inquiryType && <span style={{ padding: '1px 7px', borderRadius: '99px', background: inquiryType.color + '18', color: inquiryType.color, fontSize: '9px', fontWeight: '700' }}>{inquiryType.label}</span>}
                    <span>{i.phoneMobile || i.phoneOffice || '—'}</span>
                    <span>{fmtDate(i.createdAt)}</span>
                  </div>
                </div>
                <ChevronRight size={16} strokeWidth={2} color='#cfc5b8' />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── ALL INTAKES ───────────────────────────────────────────────────────────────
function AllIntakes({ intakes, onSelect, onBack }) {
  const [q, setQ] = useState('');
  const filtered = !q.trim() ? intakes : intakes.filter(i =>
    (i.customerName || '').toLowerCase().includes(q.toLowerCase()) ||
    (i.phoneMobile || '').includes(q) || (i.phoneOffice || '').includes(q)
  );
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
        <button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button>
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, color: '#2B2620' }}>All Intakes</div>
          <div style={{ fontSize: '12px', color: '#8C8275', marginTop: '2px' }}>{intakes.length} total records</div>
        </div>
      </div>
      <div style={card}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(139,69,19,0.06)' }}>
          <input value={q} onChange={e => setQ(e.target.value)} name="twp-intake-search" autoComplete="off" placeholder="Search by name or phone…" style={{ ...inp, fontSize: '13px' }} />
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#bbb', fontSize: '13px' }}>No results found.</div>
        ) : filtered.map((i, idx) => {
          const inquiryType = INQUIRY_TYPES.find(t => t.value === i.inquiryType);
          return (
            <div key={i.id} onClick={() => onSelect(i)}
              style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '12px 18px', cursor: 'pointer', borderBottom: idx < filtered.length - 1 ? '1px solid rgba(139,69,19,0.06)' : 'none', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fdf7f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: '34px', height: '34px', borderRadius: '9px', flexShrink: 0, background: '#fdf5ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', color: BROWN }}>{initials(i.customerName)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#2B2620', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.customerName || '—'}</div>
                <div style={{ fontSize: '11px', color: '#8C8275', display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '1px 7px', borderRadius: '99px', background: '#fdf5ee', color: BROWN, fontSize: '9px', fontWeight: '700', textTransform: 'uppercase' }}>{i.contactType || 'walk-in'}</span>
                  {inquiryType && <span style={{ padding: '1px 7px', borderRadius: '99px', background: inquiryType.color + '18', color: inquiryType.color, fontSize: '9px', fontWeight: '700' }}>{inquiryType.label}</span>}
                  <span>{i.phoneMobile || i.phoneOffice || '—'}</span>
                  <span>{fmtDate(i.createdAt)}</span>
                </div>
              </div>
              <ChevronRight size={16} strokeWidth={2} color='#cfc5b8' />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── NEW INTAKE ────────────────────────────────────────────────────────────────
function NewIntake({ onBack, onSaved }) {
  const [form, setForm] = useState({
    customerName:'', addressLine1:'', addressLine2:'', city:'', state:'SD', zip:'',
    phoneMobile:'', phoneOffice:'', phoneOther:'', email:'',
    contactType:'walk-in', message:'', inquiryType:'',
    bestTimeToCall:'', bestTimeAmPm:'',
  });
  const [flags, setFlags] = useState({});
  const [repairTypes, setRepairTypes] = useState({});   // { windows: true, siding: false, ... }
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleFlag = k => setFlags(f => ({ ...f, [k]: !f[k] }));
  const toggleRepairType = k => setRepairTypes(r => ({ ...r, [k]: !r[k] }));
  const showToast = (message, kind = 'success') => { setToast({ message, kind }); setTimeout(() => setToast(null), 2800); };
  const CT = ['walk-in', 'phone', 'email'];

  const selectedType = INQUIRY_TYPES.find(t => t.value === form.inquiryType);
  const isRepair = form.inquiryType === 'repair';
  const hasAnyRepairType = isRepair && Object.values(repairTypes).some(v => v);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.customerName.trim()) return;

    // Validation: Repair must have at least one repair type selected
    if (isRepair && !hasAnyRepairType) {
      showToast('Please select at least one Repair Type', 'warning');
      return;
    }

    setSaving(true);
    try {
      // Serialize repair types as a clean object on the intake record
      const payload = {
        ...form,
        ...flags,
        source: 'scheduler',
        repairTypes: isRepair ? repairTypes : null,
      };
      await createIntake(payload);
      showToast('Intake saved!', 'success');
      setTimeout(() => onSaved(), 900);
    } catch (err) {
      console.error(err);
      showToast('Save failed', 'error');
      setSaving(false);
    }
  }

  return (
    <div>
      <Toast toast={toast} />
      <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'20px' }}>
        <button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize:'20px', fontWeight:600, color:'#2B2620' }}>New Intake</div>
      </div>
      <form onSubmit={handleSave} autoComplete="off" {...noAutofillContainer}>

        {/* Inquiry Type */}
        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Inquiry Type <span style={{ color:'#A13D2D' }}>*</span></div>
        <div style={{ ...card, padding:'14px 18px', marginBottom:'20px' }}>
          <InquiryTypePicker value={form.inquiryType} onChange={v => setForm(f => ({ ...f, inquiryType: v }))} />
          {!form.inquiryType && <div style={{ fontSize:'11px', color:AMBER, marginTop:'8px', fontStyle:'italic' }}>Select inquiry type — routes this to the right person.</div>}
          {selectedType?.printOnly && (
            <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:AMBER, marginTop:'8px', fontWeight:'600' }}>
              <Wrench size={12} strokeWidth={2} /> Repair inquiries are <strong>print-only</strong> — after saving, print the PDF and hand it to {selectedType.rep}.
            </div>
          )}
        </div>

        {/* Repair-only fields */}
        {isRepair && (
          <RepairFieldsBlock
            repairTypes={repairTypes}
            bestTimeToCall={form.bestTimeToCall}
            bestTimeAmPm={form.bestTimeAmPm}
            onToggleType={toggleRepairType}
            onChangeBestTime={v => setForm(f => ({ ...f, bestTimeToCall: v }))}
            onChangeAmPm={v => setForm(f => ({ ...f, bestTimeAmPm: v }))}
          />
        )}

        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Customer</div>
        <div style={card}>
          <div style={{ padding:'0 18px' }}>
            {[['customerName','Full Name *',true],['addressLine1','Street Address',false],['addressLine2','Unit / Apt',false]].map(([k,lbl,req],i,arr) => (
              <FL key={k} label={lbl} style={{ padding:'13px 0', borderBottom: i<arr.length-1?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]} onChange={set(k)} required={req} name={`twp-intake-${k}`} {...noAutofill} placeholder={k==='customerName'?'First and last name':k==='addressLine1'?'123 Main St':'Optional'} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'15px', fontWeight:'500', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr' }}>
            {[['city','City','Milbank'],['state','State','SD'],['zip','ZIP','57252']].map(([k,lbl,ph],i) => (
              <FL key={k} label={lbl} style={{ padding:'13px 16px', borderTop:'1px solid rgba(139,69,19,0.08)', borderRight:i<2?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]} onChange={set(k)} name={`twp-intake-${k}`} {...noAutofill} placeholder={ph} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'14px', fontWeight:'500', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
        </div>

        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Phone</div>
        <div style={card}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
            {[['phoneMobile','Mobile / Cell'],['phoneOffice','Office']].map(([k,lbl],i) => (
              <FL key={k} label={lbl} style={{ padding:'13px 16px', borderRight:i===0?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]} onChange={set(k)} inputMode="tel" name={`twp-intake-${k}`} {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'14px', fontWeight:'500', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
          <FL label="Other" style={{ padding:'13px 16px', borderTop:'1px solid rgba(139,69,19,0.08)' }}>
            <input value={form.phoneOther} onChange={set('phoneOther')} inputMode="tel" name="twp-intake-phoneOther" {...noAutofill} placeholder="Optional" style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'14px', fontWeight:'500', background:'transparent', width:'100%' }} />
          </FL>
          <FL label="Email" style={{ padding:'13px 16px', borderTop:'1px solid rgba(139,69,19,0.08)' }}>
            <input value={form.email||''} onChange={set('email')} inputMode="email" name="twp-intake-email" {...noAutofill} placeholder="customer@email.com (optional)" style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'14px', fontWeight:'500', background:'transparent', width:'100%' }} />
          </FL>
        </div>

        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Contact Type</div>
        <div style={{ ...card, padding:'14px 18px', display:'flex', gap:'8px' }}>
          {CT.map(t => (
            <button key={t} type="button" onClick={() => setForm(f => ({ ...f, contactType: t }))}
              style={{ padding:'7px 18px', borderRadius:'99px', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', cursor:'pointer', border: form.contactType===t?'none':'1.5px solid rgba(139,69,19,0.2)', background: form.contactType===t?BROWN:'transparent', color: form.contactType===t?'#fff':'#8C8275', transition:'all .14s', boxShadow: form.contactType===t?'0 2px 8px rgba(139,69,19,.28)':'none' }}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Message Flags</div>
        <div style={{ ...card, padding:'14px 18px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'7px' }}>
            {FLAG_KEYS.map(([k,lbl]) => (
              <div key={k} onClick={() => toggleFlag(k)}
                style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 12px', borderRadius:'10px', border:`1.5px solid ${flags[k]?BROWN:'rgba(139,69,19,0.15)'}`, background:flags[k]?'#fdf5ee':'#faf8f5', cursor:'pointer', fontSize:'13px', fontWeight:'600', color:flags[k]?BROWN:'#8C8275', transition:'all .14s', userSelect:'none' }}>
                <div style={{ width:'17px', height:'17px', borderRadius:'4px', flexShrink:0, border:`1.5px solid ${flags[k]?BROWN:'#cfc5b8'}`, background:flags[k]?BROWN:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {flags[k] && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                {lbl}
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>{isRepair ? 'Description of Problem' : 'Message & Notes'}</div>
        <div style={card}>
          <textarea value={form.message} onChange={set('message')} rows={5} placeholder={isRepair ? "Describe the repair needed — what's broken, any specifics, urgency…" : "What does the customer want? Any details, urgency, or special notes…"}
            style={{ ...inp, border:'none', borderRadius:'0', resize:'none', lineHeight:'1.55', minHeight:'100px', padding:'14px 18px' }} />
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', paddingBottom:'16px' }}>
          <button type="submit" disabled={saving || !form.customerName.trim() || (isRepair && !hasAnyRepairType)}
            style={{ height:'48px', padding:'0 36px', background:BROWN, color:'#fff', border:'none', borderRadius:'99px', fontFamily:'inherit', fontSize:'14px', fontWeight:'700', cursor:saving?'not-allowed':'pointer', opacity:(saving || (isRepair && !hasAnyRepairType)) ? 0.6 : 1, boxShadow:'0 3px 14px rgba(139,69,19,.32)' }}>
            {saving ? 'Saving…' : 'Save Intake'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── INTAKE DETAIL ─────────────────────────────────────────────────────────────
function IntakeDetail({ intake, onBack, onDeleted, onUpdated }) {
  const [editing, setEditing]         = useState(false);
  const [form, setForm]               = useState({ ...intake });
  const [flags, setFlags]             = useState(() => Object.fromEntries(FLAG_KEYS.map(([k]) => [k, !!intake[k]])));
  const [repairTypes, setRepairTypes] = useState(() => intake.repairTypes || {});
  const [saving, setSaving]           = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [sending, setSending]         = useState(false);
  const [toast, setToast]             = useState(null);
  const showToast = (message, kind = 'success') => { setToast({ message, kind }); setTimeout(() => setToast(null), 3200); };
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleFlag = k => setFlags(f => ({ ...f, [k]: !f[k] }));
  const toggleRepairType = k => setRepairTypes(r => ({ ...r, [k]: !r[k] }));
  const allFlags = getFlags(intake);
  const addr = [intake.addressLine1, intake.addressLine2, [intake.city, intake.state, intake.zip].filter(Boolean).join(', ')].filter(Boolean).join(', ');
  const ini = initials(intake.customerName);
  const CT = ['walk-in','phone','email'];
  const isRepair = form.inquiryType === 'repair';
  const hasAnyRepairType = isRepair && Object.values(repairTypes).some(v => v);

  async function handleSave(e) {
    e.preventDefault();
    if (isRepair && !hasAnyRepairType) {
      showToast('Please select at least one Repair Type', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ...flags,
        repairTypes: isRepair ? repairTypes : null,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, 'intakes', intake.id), payload);
      showToast('Updated!', 'success');
      setEditing(false);
      onUpdated({ ...intake, ...form, ...flags, repairTypes: isRepair ? repairTypes : null });
    } catch (err) { console.error(err); showToast('Save failed', 'error'); setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await deleteDoc(doc(db, 'intakes', intake.id)); onDeleted(); }
    catch (err) { console.error(err); showToast('Delete failed', 'error'); setDeleting(false); setShowConfirm(false); }
  }

  async function handleSendToSales() {
    if (!intake.inquiryType) {
      showToast('Set inquiry type before sending.', 'warning');
      return;
    }
    const route = INQUIRY_TYPES.find(t => t.value === intake.inquiryType);
    if (route?.printOnly) {
      showToast('Repairs are print-only — use the Print button.', 'warning');
      return;
    }
    setSending(true);
    try {
      const functions = getFunctions();
      const sendIntakeToSales = httpsCallable(functions, 'sendIntakeToSales');
      const pdfBlob = await generateIntakePDF(intake);
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      const uint8 = new Uint8Array(pdfArrayBuffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < uint8.length; i += chunkSize) {
        binary += String.fromCharCode(...uint8.subarray(i, i + chunkSize));
      }
      const pdfBase64 = btoa(binary);
      await sendIntakeToSales({ intake, inquiryType: intake.inquiryType, pdfBase64 });
      await updateDoc(doc(db, 'intakes', intake.id), { salesEmailSentAt: serverTimestamp() });
      onUpdated({ ...intake, salesEmailSentAt: new Date() });
      showToast(`Sent to ${route?.rep || 'sales team'}!`, 'success');
    } catch (err) {
      console.error('Send to sales failed:', err);
      showToast('Failed to send — check connection', 'error');
    } finally {
      setSending(false);
    }
  }

  if (editing) return (
    <div>
      <Toast toast={toast} />
      <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'20px' }}>
        <button style={mkBtn('ghost')} onClick={() => setEditing(false)}><ArrowLeft size={13} strokeWidth={2} /> Cancel</button>
        <div style={{ fontSize:'20px', fontWeight:'800', color:'#2B2620' }}>Edit Intake</div>
      </div>
      <form onSubmit={handleSave} autoComplete="off" {...noAutofillContainer}>
        <div style={{ fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.12em', color:'#8C8275', marginBottom:'7px' }}>Inquiry Type</div>
        <div style={{ ...card, padding:'14px 18px', marginBottom:'16px' }}>
          <InquiryTypePicker value={form.inquiryType} onChange={v => setForm(f => ({ ...f, inquiryType: v }))} />
        </div>

        {/* Repair-only fields in edit mode */}
        {isRepair && (
          <RepairFieldsBlock
            repairTypes={repairTypes}
            bestTimeToCall={form.bestTimeToCall}
            bestTimeAmPm={form.bestTimeAmPm}
            onToggleType={toggleRepairType}
            onChangeBestTime={v => setForm(f => ({ ...f, bestTimeToCall: v }))}
            onChangeAmPm={v => setForm(f => ({ ...f, bestTimeAmPm: v }))}
          />
        )}

        <div style={card}>
          <div style={{ padding:'0 18px' }}>
            {[['customerName','Full Name *'],['addressLine1','Street'],['addressLine2','Unit']].map(([k,lbl],i,arr) => (
              <FL key={k} label={lbl} style={{ padding:'12px 0', borderBottom:i<arr.length-1?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]||''} onChange={set(k)} required={k==='customerName'} name={`twp-intake-edit-${k}`} {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'14px', fontWeight:'500', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr' }}>
            {[['city','City'],['state','State'],['zip','ZIP']].map(([k,lbl],i) => (
              <FL key={k} label={lbl} style={{ padding:'12px 16px', borderTop:'1px solid rgba(139,69,19,0.08)', borderRight:i<2?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]||''} onChange={set(k)} name={`twp-intake-edit-${k}`} {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'13px', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
        </div>

        {/* Phone block */}
        <div style={{ ...card, padding:'0' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
            {[['phoneMobile','Mobile / Cell'],['phoneOffice','Office']].map(([k,lbl],i) => (
              <FL key={k} label={lbl} style={{ padding:'12px 16px', borderRight:i===0?'1px solid rgba(139,69,19,0.08)':'none' }}>
                <input value={form[k]||''} onChange={set(k)} inputMode="tel" name={`twp-intake-edit-${k}`} {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'13px', background:'transparent', width:'100%' }} />
              </FL>
            ))}
          </div>
          <FL label="Other" style={{ padding:'12px 16px', borderTop:'1px solid rgba(139,69,19,0.08)' }}>
            <input value={form.phoneOther||''} onChange={set('phoneOther')} inputMode="tel" name="twp-intake-edit-phoneOther" {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'13px', background:'transparent', width:'100%' }} />
          </FL>
          <FL label="Email" style={{ padding:'12px 16px', borderTop:'1px solid rgba(139,69,19,0.08)' }}>
            <input value={form.email||''} onChange={set('email')} inputMode="email" name="twp-intake-edit-email" {...noAutofill} style={{ border:'none', outline:'none', fontFamily:'inherit', fontSize:'13px', background:'transparent', width:'100%' }} />
          </FL>
        </div>

        <div style={{ ...card, padding:'14px 18px', display:'flex', gap:'8px' }}>
          {CT.map(t => (
            <button key={t} type="button" onClick={() => setForm(f=>({...f,contactType:t}))}
              style={{ padding:'7px 16px', borderRadius:'99px', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', cursor:'pointer', border:form.contactType===t?'none':'1.5px solid rgba(139,69,19,0.2)', background:form.contactType===t?BROWN:'transparent', color:form.contactType===t?'#fff':'#8C8275' }}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ ...card, padding:'14px 18px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'7px' }}>
            {FLAG_KEYS.map(([k,lbl]) => (
              <div key={k} onClick={() => toggleFlag(k)}
                style={{ display:'flex', alignItems:'center', gap:'8px', padding:'9px 11px', borderRadius:'9px', border:`1.5px solid ${flags[k]?BROWN:'rgba(139,69,19,0.15)'}`, background:flags[k]?'#fdf5ee':'#faf8f5', cursor:'pointer', fontSize:'12px', fontWeight:'600', color:flags[k]?BROWN:'#8C8275', userSelect:'none' }}>
                <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0, border:`1.5px solid ${flags[k]?BROWN:'#cfc5b8'}`, background:flags[k]?BROWN:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {flags[k] && <svg width="9" height="7" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                {lbl}
              </div>
            ))}
          </div>
        </div>
        <div style={card}>
          <textarea value={form.message||''} onChange={set('message')} rows={5} placeholder="Message & notes…"
            style={{ ...inp, border:'none', borderRadius:'0', resize:'none', lineHeight:'1.55', padding:'14px 18px' }} />
        </div>
        <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', paddingBottom:'16px' }}>
          <button type="button" style={mkBtn('ghost')} onClick={() => setEditing(false)}>Cancel</button>
          <button type="submit" disabled={saving || (isRepair && !hasAnyRepairType)} style={{ height:'44px', padding:'0 28px', background:BROWN, color:'#fff', border:'none', borderRadius:'99px', fontFamily:'inherit', fontSize:'13px', fontWeight:'700', cursor:saving?'not-allowed':'pointer', opacity:(saving || (isRepair && !hasAnyRepairType)) ? 0.6 : 1 }}>{saving?'Saving…':'Save Changes'}</button>
        </div>
      </form>
    </div>
  );

  const salesRoute = INQUIRY_TYPES.find(t => t.value === intake.inquiryType);
  const alreadySent = !!intake.salesEmailSentAt;
  const isPrintOnly = salesRoute?.printOnly === true;

  // Summary strings for repair details in the detail view
  const selectedRepairTypes = Object.entries(intake.repairTypes || {})
    .filter(([, v]) => v)
    .map(([k]) => REPAIR_TYPES.find(rt => rt.value === k)?.label || k);

  return (
    <div>
      <Toast toast={toast} />

      {showConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div style={{ background:PAPER, borderRadius:'16px', padding:'28px 24px', maxWidth:'340px', width:'100%', textAlign:'center', position:'relative', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:'4px', background:RUST }} />
            <Trash2 size={30} strokeWidth={1.5} color={RUST} style={{ marginBottom:'12px' }} />
            <div style={{ fontFamily: FONT_DISPLAY, fontSize:'18px', fontWeight:600, color:INK, marginBottom:'6px' }}>Delete Intake?</div>
            <div style={{ fontSize:'13px', color:STONE, marginBottom:'22px', lineHeight:'1.5' }}>Remove <strong style={{ color:INK }}>{intake.customerName}</strong>'s record?<br/>Cannot be undone.</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => setShowConfirm(false)} style={{ flex:1, padding:'12px', border:'1.5px solid #e5ddd4', borderRadius:'10px', background:'none', fontSize:'13px', fontWeight:'700', color:STONE, cursor:'pointer', fontFamily:FONT_SANS }}>Keep It</button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex:1, padding:'12px', border:'none', borderRadius:'10px', background:RUST, color:'#fff', fontSize:'13px', fontWeight:'800', cursor:'pointer', fontFamily:FONT_SANS, opacity:deleting?0.6:1 }}>{deleting?'Deleting…':'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px', flexWrap:'wrap' }}>
        <button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize:'20px', fontWeight:'800', color:'#2B2620' }}>{intake.customerName}</div>
          <div style={{ fontSize:'11px', color:'#8C8275', fontFamily:'monospace', marginTop:'2px' }}>{intake.id}</div>
        </div>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          <button style={mkBtn('ghost')} onClick={() => setEditing(true)}>Edit</button>
          <button
            onClick={() => printIntakeAsPDF(intake)}
            style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px', background: isPrintOnly ? AMBER : BROWN, color: 'white', boxShadow: isPrintOnly ? '0 3px 10px rgba(180,83,9,0.35)' : '0 3px 10px rgba(139,69,19,0.35)', WebkitTapHighlightColor: 'transparent', userSelect: 'none' }}
            onPointerDown={e => e.currentTarget.style.opacity='0.75'}
            onPointerUp={e => e.currentTarget.style.opacity='1'}
            onPointerLeave={e => e.currentTarget.style.opacity='1'}
          ><Printer size={14} strokeWidth={2} /> {isPrintOnly ? 'Print for Justin' : 'Print / Save PDF'}</button>

          {!isPrintOnly && (
            <button
              disabled={sending}
              onClick={handleSendToSales}
              style={{
                ...mkBtn('teal', sending),
                position: 'relative',
                minWidth: '140px',
              }}>
              {sending ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> : alreadySent ? <CheckCircle2 size={14} strokeWidth={2} /> : <Send size={14} strokeWidth={2} />}
              {sending ? 'Sending…' : alreadySent ? 'Resend to Sales' : 'Send to Sales'}
            </button>
          )}

          <button style={mkBtn('danger')} onClick={() => setShowConfirm(true)}>Delete</button>
        </div>
      </div>

      {/* Inquiry type + routing banner */}
      {salesRoute && !isPrintOnly && (
        <div style={{ background: salesRoute.color + '12', border: `1.5px solid ${salesRoute.color}33`, borderRadius: '12px', padding: '12px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ display: 'inline-flex', color: salesRoute.color }}><salesRoute.Icon size={22} strokeWidth={2} /></span>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: salesRoute.color }}>{salesRoute.label}</div>
            <div style={{ fontSize: '11px', color: '#7a5a3a', marginTop: '2px' }}>
              Will be sent to <strong>{salesRoute.rep}</strong> + Mike
              {alreadySent && (
                <span style={{ marginLeft: '8px', color: MOSS, fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: MOSS, display: 'inline-block' }} />
                  <CheckCircle2 size={11} strokeWidth={2} /> Email sent {fmtDate(intake.salesEmailSentAt)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {salesRoute && isPrintOnly && (
        <div style={{ background: salesRoute.color + '12', border: `1.5px solid ${salesRoute.color}55`, borderRadius: '12px', padding: '12px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ display: 'inline-flex', color: salesRoute.color }}><salesRoute.Icon size={22} strokeWidth={2} /></span>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: salesRoute.color }}>{salesRoute.label} — Print Only</div>
            <div style={{ fontSize: '11px', color: '#7a5a3a', marginTop: '2px' }}>
              Print this intake and hand it to <strong>{salesRoute.rep}</strong>. No email is sent.
            </div>
          </div>
        </div>
      )}
      {!salesRoute && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: `${AMBER}12`, border: `1.5px solid ${AMBER}55`, borderRadius: '12px', padding: '12px 18px', marginBottom: '16px', fontSize: '13px', color: AMBER, fontWeight: '600' }}>
          <AlertTriangle size={15} strokeWidth={2} /> No inquiry type set — edit this intake to add one.
        </div>
      )}

      {/* Repair details card — shown in detail view only for repairs */}
      {isPrintOnly && (
        <div style={{ background:'#fffaf3', border:`1.5px solid ${AMBER}44`, borderRadius:'12px', padding:'14px 18px', marginBottom:'16px' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:AMBER, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>Repair Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 20px' }}>
            <div>
              <div style={{ fontSize:'10px', color:'#8C8275', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'3px' }}>Repair Type</div>
              <div style={{ fontSize:'13px', color:'#2B2620', fontWeight:'600' }}>
                {selectedRepairTypes.length ? selectedRepairTypes.join(' · ') : <span style={{ color:'#cc6600', fontStyle:'italic', fontWeight:'400' }}>Not set — edit to add</span>}
              </div>
            </div>
            <div>
              <div style={{ fontSize:'10px', color:'#8C8275', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'3px' }}>Best Time to Call</div>
              <div style={{ fontSize:'13px', color:'#2B2620', fontWeight:'600' }}>
                {intake.bestTimeToCall || intake.bestTimeAmPm
                  ? `${intake.bestTimeToCall || ''}${intake.bestTimeToCall && intake.bestTimeAmPm ? ' ' : ''}${intake.bestTimeAmPm || ''}`.trim()
                  : <span style={{ color:'#bbb', fontStyle:'italic', fontWeight:'400' }}>—</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:'#fff8f3', border:'1px solid #f0d8c0', borderRadius:'12px', padding:'18px', marginBottom:'16px', display:'flex', gap:'16px' }}>
        <div style={{ width:'46px', height:'46px', borderRadius:'50%', background:BROWN, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15pt', fontWeight:'700', flexShrink:0 }}>{ini}</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'16px', fontWeight:'800', color:'#2B2620', marginBottom:'4px' }}>{intake.customerName}</div>
          {addr && <div style={{ fontSize:'12px', color:'#8C8275', lineHeight:'1.7', marginBottom:'4px' }}>{addr}</div>}
          {intake.phoneMobile && <div style={{ fontSize:'12px', color:'#8C8275' }}><span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.1em', color:'#c8902a', marginRight:'6px' }}>Mobile</span>{intake.phoneMobile}</div>}
          {intake.phoneOffice && <div style={{ fontSize:'12px', color:'#8C8275' }}><span style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.1em', color:'#c8902a', marginRight:'6px' }}>Office</span>{intake.phoneOffice}</div>}
        </div>
        <div style={{ textAlign:'right' }}>
          <span style={{ padding:'3px 10px', borderRadius:'99px', background:'#fdf5ee', color:BROWN, fontSize:'11px', fontWeight:'700', textTransform:'capitalize', border:'1px solid #f0d0b0' }}>{intake.contactType||'walk-in'}</span>
          <div style={{ fontSize:'11px', color:'#8C8275', marginTop:'6px' }}>{fmtDate(intake.createdAt)}</div>
        </div>
      </div>

      <div style={{ display:'flex', gap:'14px' }}>
        <div style={{ flex:1.3, display:'flex', flexDirection:'column', gap:'14px' }}>
          <div style={card}>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid rgba(139,69,19,0.06)', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.14em', color:'#8C8275' }}>Message Flags</div>
            <div style={{ padding:'14px 18px', display:'flex', flexWrap:'wrap', gap:'7px' }}>
              {allFlags.length ? allFlags.map(f => <span key={f} style={{ padding:'4px 13px', borderRadius:'3px', background:'#fff3ea', color:'#7a3b10', fontSize:'12px', fontWeight:'600', border:'1px solid #f0d0b0' }}>{f}</span>) : <span style={{ color:'#bbb', fontSize:'12px', fontStyle:'italic' }}>No flags selected.</span>}
            </div>
          </div>
          <div style={{ ...card, flex:1 }}>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid rgba(139,69,19,0.06)', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.14em', color:'#8C8275' }}>{isPrintOnly ? 'Description of Problem' : 'Message & Notes'}</div>
            <div style={{ padding:'14px 18px', fontSize:'14px', lineHeight:'1.65', whiteSpace:'pre-wrap', minHeight:'60px', color: intake.message ? '#2B2620' : '#bbb', fontStyle: intake.message ? 'normal' : 'italic' }}>{intake.message || 'No message recorded.'}</div>
          </div>
        </div>
        <div style={{ width:'220px', flexShrink:0 }}>
          <div style={card}>
            <div style={{ padding:'12px 18px', borderBottom:'1px solid rgba(139,69,19,0.06)', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.14em', color:'#8C8275' }}>Record Info</div>
            <div style={{ padding:'0 18px' }}>
              {[
                ['Date / Time', fmtDate(intake.createdAt)],
                ['Contact', (intake.contactType||'walk-in').charAt(0).toUpperCase()+(intake.contactType||'walk-in').slice(1)],
                ['Inquiry', salesRoute ? salesRoute.label : '—'],
                ['Routes to', salesRoute ? (isPrintOnly ? `${salesRoute.rep} (print only)` : `${salesRoute.rep} + Mike`) : '—'],
                ['Source', 'Office Intake'],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', padding:'9px 0', borderBottom:'1px solid rgba(139,69,19,0.06)' }}>
                  <div style={{ width:'45%', fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', color:'#8C8275', paddingTop:'1px' }}>{k}</div>
                  <div style={{ flex:1, fontSize:'12px', color:'#2B2620', fontWeight:'500' }}>{v}</div>
                </div>
              ))}
              <div style={{ padding:'9px 0' }}>
                <div style={{ fontSize:'9px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', color:'#8C8275', marginBottom:'3px' }}>Reference</div>
                <div style={{ fontSize:'10px', fontFamily:'monospace', color:'#8C8275', wordBreak:'break-all' }}>{intake.id}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════
export default function OfficeIntakePage() {
  const [view, setView]         = useState('dashboard');
  const [intakes, setIntakes]   = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'intakes'), orderBy('createdAt', 'desc'), limit(200));
    return onSnapshot(q, snap => setIntakes(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const breadcrumb = `Dashboard › Office Intake${view==='new'?' › New':view==='all'?' › All':view==='detail'?' › '+(selected?.customerName||''):''}`;

  return (
    <AppLayout title="Office Intake" breadcrumb={breadcrumb}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontFamily: FONT_SANS }}>
        {view === 'dashboard' && <Dashboard intakes={intakes} onNew={() => setView('new')} onViewAll={() => setView('all')} onSelect={i => { setSelected(i); setView('detail'); }} />}
        {view === 'all'       && <AllIntakes intakes={intakes} onSelect={i => { setSelected(i); setView('detail'); }} onBack={() => setView('dashboard')} />}
        {view === 'new'       && <NewIntake onBack={() => setView('dashboard')} onSaved={() => setView('dashboard')} />}
        {view === 'detail' && selected && <IntakeDetail intake={selected} onBack={() => setView('all')} onDeleted={() => { setSelected(null); setView('dashboard'); }} onUpdated={u => setSelected(u)} />}
        {view !== 'new' && (
          <div style={{ position:'fixed', bottom:0, left:0, right:0, padding:'14px 20px 26px', background:'linear-gradient(to top,rgba(242,237,232,1) 55%,transparent)', display:'flex', justifyContent:'center', gap:'9px', zIndex:90, pointerEvents:'none' }}>
            {view !== 'dashboard' && <button onClick={() => setView('dashboard')} style={{ height:'46px', padding:'0 22px', borderRadius:'99px', fontFamily:FONT_SANS, fontSize:'13px', fontWeight:'700', cursor:'pointer', border:'1.5px solid rgba(139,69,19,0.2)', background:PAPER, color:STONE, boxShadow:'0 2px 8px rgba(0,0,0,.08)', pointerEvents:'all' }}>Dashboard</button>}
            <button onClick={() => setView('new')} style={{ height:'46px', padding:'0 24px', borderRadius:'99px', fontFamily:FONT_SANS, fontSize:'13px', fontWeight:'700', cursor:'pointer', border:'none', background:BROWN, color:'#fff', boxShadow:'0 4px 16px rgba(139,69,19,.36)', pointerEvents:'all' }}>+ New Intake</button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}